/**
 * Audit logs viewer component
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { listAuditLogs, type AuditLog } from '../../api/audit';
import { useWorkspace } from '../../hooks/useWorkspace';
import { FileText, Search, Filter, Download, CheckCircle, XCircle, Clock } from 'lucide-react';

export function AuditLogsViewer() {
  const { tenantId, projectId } = useWorkspace();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', tenantId, projectId, page, pageSize, actionTypeFilter],
    queryFn: () => listAuditLogs({
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
      action_type: actionTypeFilter || undefined,
      page,
      page_size: pageSize,
    }),
    enabled: !!(tenantId || projectId),
  });

  const filteredLogs = data?.logs.filter(log =>
    log.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.output?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.user_id?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (!tenantId && !projectId) {
    return (
      <Card title="Audit Logs">
        <div className="text-center py-8 text-gray-500">
          Please select a tenant or project to view audit logs
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card title="Audit Logs">
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-5 w-5 text-gray-400" />}
              />
            </div>
            <select
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Actions</option>
              <option value="query">Query</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="export">Export</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
            </select>
            <Button variant="outline">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>

          {/* Logs Table */}
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading audit logs...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No audit logs found</div>
          ) : (
            <div className="space-y-2">
              {filteredLogs.map((log) => (
                <AuditLogItem key={log.id} log={log} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {data && data.total > pageSize && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.total)} of {data.total} logs
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">Page {page}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * pageSize >= data.total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function AuditLogItem({ log }: { log: AuditLog }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-5 w-5 text-gray-400" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">{log.action_type}</div>
            <Badge variant="info">{log.resource_type}</Badge>
            {log.success ? (
              <Badge variant="success">
                <CheckCircle className="h-3 w-3" />
                Success
              </Badge>
            ) : (
              <Badge variant="error">
                <XCircle className="h-3 w-3" />
                Failed
              </Badge>
            )}
            {!log.access_granted && (
              <Badge variant="error">Access Denied</Badge>
            )}
          </div>
          
          {log.question && (
            <div className="text-sm text-gray-700 mb-1">
              <span className="font-medium">Question:</span> {log.question}
            </div>
          )}
          
          {log.output && (
            <div className="text-sm text-gray-600 mb-1 line-clamp-2">
              {log.output}
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
            {log.user_id && <span>User: {log.user_id.substring(0, 8)}...</span>}
            {log.model_name && <span>Model: {log.model_name}</span>}
            {log.duration_ms && <span>Duration: {log.duration_ms}ms</span>}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(log.created_at).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}











